<script setup>
import CitiesModal from "@/components/Modals/CitiesModal.vue";
import TemplateMain from "@/components/Layouts/MainLayout/TemplateMain.vue";
import TemplateHeader from "@/components/Layouts/MainLayout/TemplateHeader.vue";
import TemplateMobileNavBar from "@/components/Layouts/MainLayout/TemplateMobileNavBar.vue";
import { computed, onBeforeUnmount, onMounted } from "vue";
import { useStore } from "vuex";
import ShareModal from "@/components/ShareModal.vue";
import FloatingMobileWrapper from "@/components/Ui/FloatingMobileWrapper.vue";
import TemplateMobileHeader from "@/components/Layouts/MainLayout/TemplateMobileHeader.vue";
import { useRoute, useRouter } from "vue-router";
import moment from "moment";
import TemplateHeaderUnAuth from "@/components/Layouts/MainLayout/TemplateHeaderUnAuth.vue";
import { disableFirebasePush, requestFirebaseToken } from "@/firebase";

const { push } = useRouter();
const route = useRoute();

const store = useStore();

const noticeTimer = () => {
  const today = new Date();
  today.setHours(14, 0, 0);

  const todayFormat = moment(today).format("HH:mm:ss");

  const isNoticeToday = localStorage.getItem("isNoticeToday");
  const noticeTodayFormat = moment(isNoticeToday).format("HH:mm:ss");

  const rangeHour = moment
    .utc(moment.duration(todayFormat) - moment.duration(noticeTodayFormat))
    .format("HH");
  const rangeMinute = moment
    .utc(moment.duration(todayFormat) - moment.duration(noticeTodayFormat))
    .format("mm");
  const rangeSecond = moment
    .utc(moment.duration(todayFormat) - moment.duration(noticeTodayFormat))
    .format("ss");

  const numberRangeHour = parseInt(rangeHour);
  const numberRangeMinute = parseInt(rangeMinute);
  const numberRangeSecond = parseInt(rangeSecond);
  const range =
    numberRangeHour * 3600000 +
    numberRangeMinute * 60000 +
    numberRangeSecond * 1000;

  if (noticeTodayFormat < todayFormat) {
    setTimeout(() => {
      store.commit("system/SET_TRIAL_TARIFF_NOTICE", true);
    }, range);
  }
};

const getInfoUser = async () => {
  const isIOS = /iPhone|iPad/i.test(navigator.userAgent);
  const isStandalone = matchMedia("(display-mode: standalone)").matches;

  const enablePush = async (user) => {
    const token = await requestFirebaseToken(user);
    if (token) {
      localStorage.setItem("push_enabled", "true");
      localStorage.setItem("push_key", token);
    }
  };

  const disablePush = async () => {
    await disableFirebasePush();
    localStorage.setItem("push_enabled", "false");
    localStorage.removeItem("push_key");
  };

  if (localStorage.token) {
    try {
      await store.dispatch("user/loadUserInformation");

      const user = store.state.user.user;
      if (!user.first_name || !user.last_name || !user.email) {
        await push({ name: "LoginView" });
        return;
      }

      const hasDevices = user.devices.length > 0;
      const hasPushKey = !!localStorage.push_key;

      if (hasDevices && !hasPushKey) {
        if (!isIOS || (isIOS && isStandalone)) {
          await enablePush(user);
        }
      } else if (!hasDevices && hasPushKey) {
        if (!isIOS || (isIOS && isStandalone)) {
          await disablePush();
        }
      }

      await Promise.all([
        store.dispatch("user/loadUserPromoCodes"),
        store.dispatch("tariffs/loadTariffsList"),
      ]);

      if (user.tariff_id === 1) {
        noticeTimer();
      }

      await store.dispatch("notifications/init");
      await store.dispatch("notifications/getUnreadCount");
    } catch (err) {
      console.error("Ошибка получения информации о пользователе:", err);
    } finally {
      localStorage.setItem("isNoticeToday", moment().toISOString());
    }
  } else {
    const blogRoutes = ["BlogView", "BlogPage", "BlogPostPage"];
    const docRoutes = ["DocumentsLab", "DocumentsLabTemplate"];

    if (blogRoutes.includes(route.name)) {
      await push({
        name: route.name,
        params: route.params,
        query: route.query,
      });
    } else if (docRoutes.includes(route.name)) {
      await push({ name: route.name });
    }
  }
};

const updateServiceWorker = async () => {
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
    }
  }
};

const handleOnline = () => {
  updateServiceWorker();
};

onMounted(() => {
  getInfoUser();

  if (navigator.onLine) {
    updateServiceWorker();
  }

  window.addEventListener("online", handleOnline);
});

onBeforeUnmount(() => {
  window.removeEventListener("online", handleOnline);
});

const isAppLoaded = computed(() => {
  if (
    (!localStorage.token && route.name === "BlogView") ||
    (!localStorage.token && route.name === "BlogPage") ||
    (!localStorage.token && route.name === "BlogPostPage") ||
    (!localStorage.token && route.name === "DocumentsLab") ||
    (!localStorage.token && route.name === "DocumentsLabTemplate")
  ) {
    return true;
  } else {
    return (
      store.state.system.regions &&
      store.state.tariffs.tariffs &&
      store.state.user.user
    );
  }
});

const isAuth = computed(
  () =>
    store.state.system.regions &&
    store.state.tariffs.tariffs &&
    store.state.user.user
);

const isDocsLab = computed(() => {
  return route.name === "DocumentsLabTemplate";
});

const isShowNavBar = computed(() => {
  return (
    route.name !== "ProfileCustomTariffPayment" &&
    route.name !== "BlogPostPage" &&
    (route.name !== "ChatCurrentChannelView" ||
      (route.query?.type && route.query?.id))
  );
});
</script>

<template>
  <template v-if="isAppLoaded">
    <CitiesModal v-if="store.state.system.show_regions_modal" />
    <ShareModal />
    <TemplateHeader v-if="isAuth && !isDocsLab" />
    <TemplateHeaderUnAuth v-if="!isAuth && !isDocsLab" />
    <TemplateMobileHeader v-if="isAuth" />
    <TemplateMain />
    <FloatingMobileWrapper />
    <TemplateMobileNavBar v-if="isShowNavBar && isAuth" />
  </template>
  <Transition>
    <div v-if="!isAppLoaded" class="app-overlay">
      <img src="../../../assets/images/logo.svg" alt="logotype" />
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.app-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $background-light;
  z-index: 5;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease-in;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
